import { useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";
import { useTranslation } from "react-i18next";

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";

import { CheckboxField, InputField } from "@/features/FormCard/Fields";
import { Clients } from "@/lib/clients";
import { Tables } from "@/gen/tableNames/tables";

import {
  CancelConfirmationModal,
  ConfirmationModal,
} from "@/features/FormCard/Modals";

import { FormActionsNew } from "@/features/FormCard/Actions";

import { useEditForm } from "@/features/Edit/hooks";
import { NewDiff } from "@/features/FormCard/NewDiff";
import { Label } from "@radix-ui/react-label";

const FormSchema = z.object({
  client: z.string().optional().nullable(),
  page_name: z.string().optional().nullable(),
  tableau_view: z.string().optional().nullable(),
  tableau_workbook_id: z.string().optional().nullable(),
  pdf_report_display: z.boolean().optional().nullable(),
});

export function D1PdfPageSelectionNew() {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const client = searchParams.get("client") || Clients.D1;
  const table_name = `${Tables.D1_PDF_PAGE_SELECTION}`;

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
  } = useEditForm({
    client,
    table_name,
    FormSchema,
    isNew: true,
  });

  const navigateToList = () => {
    navigate(-1);
  };

  return (
    <div className="grid place-items-center bg-gray-50 h-full w-full">
      <div className="flex justify-center w-full max-w-card">
        <Card className="p-4 bg-white w-full ">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {t("pdf_page_selection.new_selection")}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                <Label>{t("sections.selection_info")}</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="client"
                    label={t(`${table_name}.columns.client`, {
                      ns: "tables",
                      defaultValue: "Client",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name="page_name"
                    label={t(`${table_name}.columns.page_name`, {
                      ns: "tables",
                      defaultValue: "Page name",
                    })}
                    control={form.control}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="tableau_view"
                    label={t(`${table_name}.columns.tableau_view`, {
                      ns: "tables",
                      defaultValue: "Tableau view",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name="tableau_workbook_id"
                    label={t(`${table_name}.columns.tableau_workbook_id`, {
                      ns: "tables",
                      defaultValue: "Tableau workbook ID",
                    })}
                    control={form.control}
                  />
                </div>
                <div className="grid grid-cols-1 gap-4">
                  <CheckboxField
                    name="pdf_report_display"
                    label={t(`${table_name}.columns.pdf_report_display`, {
                      ns: "tables",
                      defaultValue: "PDF report display",
                    })}
                    control={form.control}
                  />
                </div>
              </CardBody>
              <CardFooter>
                <FormActionsNew
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                />
              </CardFooter>
            </form>
          </Form>
        </Card>

        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          isLoading={isUpdating}
          onClose={cancelUpdate}
          onConfirm={confirmUpdate}
          title={t("confirm_create")}
          children={
            changedFields
              ? NewDiff(changedFields, [], undefined, table_name)
              : t("no_changes")
          }
        />

        <CancelConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={closeDeleteModal}
          onCancel={navigateToList}
        />
      </div>
    </div>
  );
}
